import {useCallback, useEffect, useRef, useState} from 'react';
import {message} from 'antd';
import {useTranslation} from 'react-i18next';
import api from '../api';

// Poll durable status rather than relying on a websocket being connected when
// a background batch completes. Existing table rows restore polling on reload.
export default function useTagDeletion(data, onChange) {
    const {t} = useTranslation();
    const [requesting, setRequesting] = useState(false);
    const [tracked, setTracked] = useState([]);
    const statuses = useRef(new Map());
    const refresh = useRef(onChange);
    refresh.current = onChange;

    const track = useCallback(deletions => {
        const ids = deletions.filter(d => d.state !== 'completed').map(d => d.id);
        setTracked(previous => [...new Set([...previous, ...ids])]);
    }, []);

    useEffect(() => {
        track(data.map(row => row.deletion).filter(Boolean));
    }, [data, track]);

    useEffect(() => {
        if (!tracked.length) return;
        let cancelled = false;
        let timer;
        const poll = async () => {
            try {
                // The API deliberately bounds each response.
                for (let offset = 0; offset < tracked.length; offset += 100) {
                    const {data: result} = await api.get('/api/tags/deletion_status', {
                        params: {ids: tracked.slice(offset, offset + 100)}
                    });
                    if (cancelled) return;
                    let changed = false;
                    for (const deletion of result.deletions) {
                        const previous = statuses.current.get(deletion.id);
                        statuses.current.set(deletion.id, deletion.state);
                        if (previous === deletion.state) continue;
                        changed = true;
                        if (deletion.state === 'completed') {
                            message.success(t('tag-deletion-completed', 'Deleted {{name}}.', {name: deletion.name}));
                            setTracked(ids => ids.filter(id => id !== deletion.id));
                        } else if (deletion.state === 'failed') {
                            message.error(t('tag-deletion-failed', 'Deletion of {{name}} was interrupted. It will retry automatically, or you can retry now.', {name: deletion.name}));
                        }
                    }
                    if (changed) refresh.current();
                }
            } catch {
                // A temporary polling failure does not mean the deletion failed.
                // Keep the visible pending state and try again.
            } finally {
                if (!cancelled) timer = setTimeout(poll, 3000);
            }
        };
        timer = setTimeout(poll, 1000);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [tracked, t]);

    const requestDeletion = async ids => {
        setRequesting(true);
        try {
            // Serialize bounded requests for selections spanning many pages.
            for (let offset = 0; offset < ids.length; offset += 100) {
                const batch = ids.slice(offset, offset + 100);
                const {data: result} = batch.length === 1
                    ? await api.delete(`/api/tags/${batch[0]}`)
                    : await api.delete('/api/tags/bulk_destroy', {params: {ids: batch}});
                track(result.deletions);
            }
            message.info(t('tag-deletion-pending', 'Deletion queued. Tags remain visible until cleanup finishes.'));
        } catch {
            message.error(t('tag-deletion-request-failed', 'Could not queue all selected tags for deletion. Refresh and retry the remaining tags.'));
        } finally {
            setRequesting(false);
            refresh.current();
        }
    };

    return {requestDeletion, requesting};
}
